import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import * as Sentry from '@sentry/react';
import i18n from '@/i18n/config';

interface Props {
  children: ReactNode;
  section?: string;
  fallbackPath?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[${this.props.section || 'Route'}] Error:`, error, errorInfo);

    try {
      Sentry.withScope((scope) => {
        scope.setTag('section', this.props.section || 'unknown');
        scope.setExtra('componentStack', errorInfo.componentStack);
        Sentry.captureException(error);
      });
    } catch {
      // Sentry may not be initialized
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  private t(key: string, fallback: string): string {
    try {
      const val = i18n.t(key);
      return val && val !== key ? val : fallback;
    } catch {
      return fallback;
    }
  }

  render() {
    if (this.state.hasError) {
      const section = this.props.section || this.t('errors.thisPage', 'this page');

      return (
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 min-h-[60vh] bg-[var(--color-navy)] text-white">
          <div className="glass-strong max-w-sm w-full text-center space-y-5 p-5 sm:p-8">
            <div className="w-14 h-14 bg-[var(--color-orange)]/15 border border-[var(--color-orange)]/40 flex items-center justify-center mx-auto">
              <AlertTriangle className="h-7 w-7 text-[var(--color-orange)]" strokeWidth={1.75} />
            </div>

            <div className="space-y-1.5">
              <h2 className="text-base sm:text-lg font-bold text-white">
                {this.t('errors.globalTitle', 'Something went wrong')}
              </h2>
              <p className="text-sm text-white/70">
                {this.t('errors.routeMessage', `An error occurred while loading ${section}. This has been reported automatically.`)}
              </p>
            </div>

            {this.state.error && (
              <details className="text-left bg-white/5 border border-white/10 p-3 text-xs">
                <summary className="cursor-pointer font-medium mb-1 text-white/80">
                  {this.t('errors.detailsToggle', 'Show error details')}
                </summary>
                <pre className="whitespace-pre-wrap font-mono text-[11px] text-white/60 mt-2">
                  {this.state.error.message}
                  {import.meta.env.DEV && this.state.error.stack
                    ? `\n\n${this.state.error.stack}`
                    : ''}
                </pre>
              </details>
            )}

            <div className="flex gap-2 justify-center pt-1">
              <Button
                size="sm"
                onClick={this.handleRetry}
                className="gap-1.5 border-2 border-[var(--color-orange)] bg-transparent text-[var(--color-orange)] font-semibold hover:bg-[var(--color-navy)] font-semibold rounded-none h-10 px-4"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {this.t('errors.tryAgain', 'Try Again')}
              </Button>
              {this.props.fallbackPath ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.location.href = this.props.fallbackPath!}
                  className="gap-1.5 bg-white/5 border-2 border-white/15 text-white hover:bg-white/10 hover:text-white rounded-none h-10 px-4"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {this.t('errors.goBack', 'Go Back')}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.location.href = '/'}
                  className="gap-1.5 bg-white/5 border-2 border-white/15 text-white hover:bg-white/10 hover:text-white rounded-none h-10 px-4"
                >
                  <Home className="h-3.5 w-3.5" />
                  {this.t('errors.goHome', 'Home')}
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
